import React, { createContext, useContext, ReactNode } from 'react';
import { useSubscription, type UseSubscriptionReturn } from '../hooks/useSubscription.ts';

// Create the subscription context
const SubscriptionContext = createContext<UseSubscriptionReturn | null>(null);

interface SubscriptionProviderProps {
  children: ReactNode;
}

/**
 * Provider component that wraps the app and provides subscription data
 * Uses the new useSubscription hook to fetch data from subscription-status endpoint
 */
export function SubscriptionProvider({ children }: SubscriptionProviderProps) {
  const subscriptionData = useSubscription();
  
  return (
    <SubscriptionContext.Provider value={subscriptionData}>
      {children}
    </SubscriptionContext.Provider>
  );
}

/**
 * Hook to access subscription data from context
 * Throws error if used outside of SubscriptionProvider
 */
export function useSub(): UseSubscriptionReturn {
  const context = useContext(SubscriptionContext);
  
  if (context === null) {
    throw new Error('useSub must be used within a SubscriptionProvider');
  }
  
  return context;
}

/**
 * Compatibility hook that maps new subscription data to legacy format
 * @deprecated Use useSub() directly instead
 */
export function useLegacyCreditsFormat() {
  const { data, loading, error, canScan, hasCredits, displayText } = useSub();
  
  // Map new format to legacy ScanCreditsStatus format for backward compatibility
  return {
    credits: data ? {
      isPro: data.plan === 'pro',
      hasCredits,
      creditsRemaining: data.quota_left ?? 0,
      canScan,
      nextResetDate: data.period_end,
      subscription: {
        id: 'legacy-compat',
        plan: data.plan,
        active: data.status === 'active',
        credits_remaining: data.quota_left ?? 0,
        current_period_end: data.period_end,
        stripe_subscription_id: undefined
      }
    } : null,
    loading,
    error,
    canScan,
    hasCredits,
    displayText,
    refresh: async () => {}, // Will be provided by the actual hook
    validatePermission: async () => ({ allowed: canScan, reason: canScan ? undefined : 'No credits available' })
  };
} 